import { Box, Divider, Grid, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { FIELDS, PICKUP_TYPE_DOCTOR, PICKUP_TYPE_HOME, RETURN_TRIP_PICKUP_DATEPICKER_LABEL } from '../constants';
import AppointmentDescription from './AppointmentDescription';
import AppointmentSection from './AppointmentSection';
import AppointmentDateTimePicker from './AppointmentDateTimePicker';
import Location from './Location';
import YesNoToggle from './YesNoToggle';

TripInfoDetailsStep.propTypes = {
  form: PropTypes.shape({
    formState: PropTypes.object.isRequired,
    formDispatch: PropTypes.func.isRequired,
  }).isRequired,
  setFields: PropTypes.func.isRequired,
  setIsTripInfoDetailsSelected: PropTypes.func.isRequired,
  restrictedDates: PropTypes.array,
  restrictedDatesLoading: PropTypes.bool,
  doctorsList: PropTypes.array,
  doctorsLoading: PropTypes.bool,
  setDoctorsRefetchTrigger: PropTypes.func,
};

export default function TripInfoDetailsStep({
  form,
  setFields,
  setIsTripInfoDetailsSelected,
  restrictedDates,
  restrictedDatesLoading,
  doctorsList,
  doctorsLoading,
  setDoctorsRefetchTrigger,
}) {
  const { formState } = form;

  const [isAllowedPickupDate, setIsAllowedPickupDate] = useState(true);
  const [isAllowedAppointmentDate, setIsAllowedAppointmentDate] = useState(true);
  const [isAllowedReturnTripPickupDate, setIsAllowedReturnTripPickupDate] = useState(true);

  const isTripInfoDetailsValid = useMemo(() => {
    const hasDateSelected =
      (formState.HasExistingAppointment === true &&
        formState.AppointmentDateTime &&
        isAllowedAppointmentDate &&
        isAllowedPickupDate &&
        (!formState.CreateReturnTrip || isAllowedReturnTripPickupDate)) ||
      (formState.HasExistingAppointment === false && !!formState.SeenByDate);

    const pickupValid = formState.PickupLocationType !== PICKUP_TYPE_DOCTOR || formState.PickupDoctorId > 0;

    const dropoffValid = formState.DropoffLocationType !== PICKUP_TYPE_DOCTOR || formState.DropoffDoctorId > 0;

    const isCreateReturnTripSelected = typeof formState.CreateReturnTrip === 'boolean';

    return hasDateSelected && isCreateReturnTripSelected && pickupValid && dropoffValid;
  }, [formState, isAllowedAppointmentDate, isAllowedPickupDate]);

  useEffect(() => {
    if (isTripInfoDetailsValid) setIsTripInfoDetailsSelected(true);
    else setIsTripInfoDetailsSelected(false);
  }, [isTripInfoDetailsValid, setIsTripInfoDetailsSelected]);

  return (
    <Stack spacing={1}>
      <AppointmentSection
        hasExistingAppointment={formState.HasExistingAppointment}
        setHasExistingAppointment={(v) => {
          if (v === true) {
            setFields({ [FIELDS.HAS_EXISTING_APPOINTMENT]: v, [FIELDS.SEEN_BY_DATE]: null });
          } else if (v === false) {
            setFields({ [FIELDS.HAS_EXISTING_APPOINTMENT]: v, [FIELDS.APPOINTMENT_DATE_TIME]: null });
          }
        }}
        pickupTime={formState.PickupTime}
        appointmentDateTime={formState.AppointmentDateTime}
        setter={(field, value) => setFields({ [field]: value })}
        seenByDate={formState.SeenByDate}
        setSeenByDate={(v) => setFields({ [FIELDS.SEEN_BY_DATE]: v })}
        restrictedDates={restrictedDates}
        restrictedDatesLoading={restrictedDatesLoading}
        setIsAllowedPickupDate={setIsAllowedPickupDate}
        setIsAllowedAppointmentDate={setIsAllowedAppointmentDate}
        FIELDS={FIELDS}
        isNewRequest
      />
      <Divider variant="middle" />
      <Grid container spacing={2}>
        <Grid item>
          <YesNoToggle
            title="Create Return Trip?"
            setter={(v) => setFields({ [FIELDS.CREATE_RETURN_TRIP]: v })}
            value={formState.CreateReturnTrip}
          />
        </Grid>
        {formState.CreateReturnTrip && (
          <Grid item>
            <AppointmentDateTimePicker
              label={RETURN_TRIP_PICKUP_DATEPICKER_LABEL}
              appointmentDateTime={formState.ReturnTripPickupTime}
              setter={(field, value) => setFields({ [field]: value })}
              field={FIELDS.RETURN_TRIP_PICKUP_TIME}
              restrictedDates={restrictedDates}
              isLoading={restrictedDatesLoading}
              setIsAllowedDate={setIsAllowedReturnTripPickupDate}
              referenceDate={formState.AppointmentDateTime}
              isNewRequest
            />
          </Grid>
        )}
      </Grid>
      <Divider variant="middle" />
      <Location
        doctorsList={doctorsList}
        isLoading={doctorsLoading}
        formState={formState}
        setFields={(field, value) => {
          if (field === FIELDS.PICKUP_LOCATION_TYPE && value === PICKUP_TYPE_HOME) {
            setFields({ [field]: value, [FIELDS.PICKUP_DOCTOR_ID]: null });
          } else {
            setFields({ [field]: value });
          }
        }}
        labelText="Pickup Location:"
        locationTypeField={FIELDS.PICKUP_LOCATION_TYPE}
        doctorIdField={FIELDS.PICKUP_DOCTOR_ID}
        refetchDoctors={() => setDoctorsRefetchTrigger((prev) => prev + 1)}
      />
      <Divider variant="middle" />
      <Location
        doctorsList={doctorsList}
        isLoading={doctorsLoading}
        formState={formState}
        setFields={(field, value) => {
          if (field === FIELDS.DROPOFF_LOCATION_TYPE && value === PICKUP_TYPE_HOME) {
            setFields({ [field]: value, [FIELDS.DROPOFF_DOCTOR_ID]: null });
          } else {
            setFields({ [field]: value });
          }
        }}
        labelText="Drop-off Location:"
        locationTypeField={FIELDS.DROPOFF_LOCATION_TYPE}
        doctorIdField={FIELDS.DROPOFF_DOCTOR_ID}
        refetchDoctors={() => setDoctorsRefetchTrigger((prev) => prev + 1)}
      />
      <Divider variant="middle" />
      <AppointmentDescription
        setFields={(e) => setFields({ [FIELDS.APPOINTMENT_DESC]: e.target.value })}
        value={formState.AppointmentDesc}
      />
    </Stack>
  );
}
